import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import IconClose from '../svg/IconClose'
import QuntityHandler from './QuntityHandler'
import { Button } from '../ui/Button'
import { CART_API_URL, HOME_API_URL } from '../../api/path'
import { useMutation, useQuery, useQueryClient } from "react-query";
import Http from '../../api/Http'
import files from '../../helpers/helper'
import authStores from "../../contexts/AuthProvider";
import { useNavigate } from 'react-router-dom'
import { PATH_AUTH, PATH_PRODUCT } from '../../routes/path'
import IconDelete from '../svg/IconDelete'
import { enqueueSnackbar } from 'notistack'
import { withTranslation } from 'react-i18next'
import useLanguage from '../../hooks/useLanguage'
import CircularProgress from '@mui/material/CircularProgress';
import base64 from 'base-64'
import ProductPriceDisplay from '../ui/ProductPriceDisplay'
import StockStatus from './StockStatus'
import { notifyCartChanged } from '../../utils/cartSync'
import SafeImage from '../common/SafeImage'
import { PriceChangedBanner, PriceChangedLine } from './CartPriceChangedNotice'
import { formatKd as formatCartTotal } from '../../utils/formatKd'

const nextCartAfterQuantityChange = (items, productId, rateId, delta) => {
    let totalAmount = 0;
    const list = [];
    for (const item of items || []) {
        const same = String(item?.product_id) === String(productId) && String(item?.rateId) === String(rateId);
        if (!same) {
            list.push(item);
            if (!item?.productUnavailable) {
                totalAmount += Number(item?.quantity || 0) * Number(item?.regular_price || 0);
            }
            continue;
        }
        const quantity = Number(item?.quantity || 0) + delta;
        if (quantity <= 0) continue;
        const available = Number(item?.availableQuantity);
        const updated = {
            ...item,
            quantity,
            exceedsStock: Number.isFinite(available) && available > 0 && quantity > available,
        };
        list.push(updated);
        if (!updated.productUnavailable) {
            totalAmount += quantity * Number(updated.regular_price || 0);
        }
    }
    return { list, totalAmount };
};

const CartSidebar = ({ cartSidebarOpen, setCartSidebarOpen, t, setCartItemCount }) => {
    const [cartList, setCartList] = useState([]);
    const [total, setTotal] = useState(0);
    const [deletedItem, setDeletedItem] = useState(null);
    const { sessionId, isAuthenticated, isGuest } = authStores((state) => ({
        sessionId: state.sessionId,
        isAuthenticated: state.isAuthenticated,
        isGuest: state.isGuest
    }));
    const queryKey = useMemo(() => ["cartListApi", isAuthenticated], [isAuthenticated]);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [quntityCount, setQuntityCount] = useState(0);
    const [id, setId] = useState(0);
    const cartListRef = useRef([]);
    const burstsInFlight = useRef(0);
    const listRequestId = useRef(0);

    const { language } = useLanguage()
    const [totalCount, setTotalCount] = useState();

    const hasUnavailable = useMemo(
        () => (cartList || []).some((item) => item?.outOfStock || item?.exceedsStock || item?.productUnavailable),
        [cartList]
    );

    useEffect(() => {
        setCartItemCount(cartList?.length);
    }, [cartList]);
    const { data: cartPayload } = useQuery(queryKey,
        async () => {
            const requestId = ++listRequestId.current;
            const response = isAuthenticated ? await Http.post(CART_API_URL.cartList) : await Http.post(HOME_API_URL.cartList, { sessionId: sessionId.toString() });
            return {
                products: response?.data?.products || [],
                overallTotal: response?.data?.overallTotal ?? 0,
                requestId,
            };
        }, {
        refetchOnWindowFocus: true,
        staleTime: 0,
    });

    // Always pull live stock when the drawer opens (checkout hold / other sales).
    useEffect(() => {
        if (!cartSidebarOpen) return;
        queryClient.invalidateQueries(queryKey);
    }, [cartSidebarOpen, queryClient, queryKey]);

    useEffect(() => {
        if (!cartPayload) return;
        if (burstsInFlight.current > 0) return;
        if (cartPayload.requestId !== listRequestId.current) return;
        cartListRef.current = cartPayload.products || [];
        setCartList(cartListRef.current);
        setTotal(formatCartTotal(cartPayload.overallTotal));
    }, [cartPayload]);

    const adjustCartQuantity = useCallback((productId, rateId, delta) => {
        const result = nextCartAfterQuantityChange(cartListRef.current, productId, rateId, delta);
        cartListRef.current = result.list;
        setCartList(result.list);
        setTotal(formatCartTotal(result.totalAmount));
    }, []);

    const handleBurstStart = useCallback(() => {
        burstsInFlight.current += 1;
    }, []);

    const handleQuantitySettled = useCallback(() => {
        burstsInFlight.current = Math.max(0, burstsInFlight.current - 1);
        if (burstsInFlight.current === 0) {
            queryClient.invalidateQueries(queryKey);
            queryClient.invalidateQueries(["checkoutApi"]);
            queryClient.invalidateQueries(["productDetailsApi"]);
            notifyCartChanged();
        }
    }, [queryClient, queryKey]);

    const handleOnCheckout = () => {
        if (hasUnavailable) {
            enqueueSnackbar("Please remove unavailable items before checkout", {
                variant: "error",
                anchorOrigin: { vertical: "top", horizontal: "right" },
                autoHideDuration: 3000,
            });
            return;
        }
        if (!isAuthenticated && !isGuest) {
            setCartSidebarOpen(false)
            navigate(PATH_AUTH.login)
        } else {
            setCartSidebarOpen(false)
            navigate(PATH_PRODUCT.checkout, { state: { total: total, quntityCount: quntityCount, id: id } })
        }
    }

    const { mutateAsync: removeToCart, isLoading: isRemove } = useMutation(
        async (data) => {
            return await isAuthenticated ? Http.post(CART_API_URL.removeCart, { ...data, type: 'delete_all' }) : Http.post(HOME_API_URL.removeCart, { ...data, sessionId: sessionId, type: 'delete_all' });
        },
        {
            onSuccess: async ({ data }) => {
                setTotalCount(data?.cartLength)

                queryClient.invalidateQueries(["cartListApi"]);
                queryClient.invalidateQueries(["checkoutApi"]);
                queryClient.invalidateQueries(["productDetailsApi"]);
                queryClient.invalidateQueries(["getWishListAPI"]);
                notifyCartChanged();

                enqueueSnackbar(t('validation_message.product_remove'), {
                    variant: "success",
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    autoHideDuration: 2000,
                });

            },
            onError: (error) => {
                const errorMessage = error?.response?.data?.message;
                if (errorMessage) {
                    enqueueSnackbar(errorMessage, {
                        variant: "error",
                        anchorOrigin: { vertical: "top", horizontal: "right" },
                        autoHideDuration: 2000,
                    });
                }
            },
        }
    );

    useEffect(() => {
        const currentUrl = window.location.pathname;
        if (totalCount == 0) {
            if (currentUrl == '/checkout') {
                window.history.back();
            }
        }
    }, [totalCount]);

    const handleDivClick = (productId) => {
        setCartSidebarOpen(false)
        navigate(PATH_PRODUCT.productDetailNew(base64.encode(productId)), { state: { productId: productId } });
    };


    return (
        <>
            {
                <div className={`sidebar fixed top-0 z-50  ${cartSidebarOpen ? "end-0" : "-end-[100%]"} w-full sm:w-[480px] h-full bg-gray-light`}>
                    <div className="sidebar-header flex items-center justify-between p-4 bg-black rtl:flex-row-reverse">
                        <h5 className='font-cormorant text-base-24 font-500 text-white'>{t("comman_text.shopping_cart")}</h5>
                        <button onClick={() => setCartSidebarOpen(false)}>
                            <IconClose className="w-6 h-6 text-white" />
                        </button>
                    </div>
                    <div className="sidebar-body p-4 h-[calc(100%-139px)] overflow-y-auto theme-scrollbar">
                        {
                            cartList.length ?
                                <div className="card-items-list">
                                    <PriceChangedBanner items={cartList} className="mb-2" />
                                    <ul>
                                        {cartList?.map((value) => (
                                            <li key={`${value.product_id}-${value.rateId}`} className='mb-2 last:mb-0'>
                                                <div className={`card-item p-4 flex items-start sm:items-center gap-md border border-black-light  last:border-b-gold-light ${value.outOfStock || value.exceedsStock || value.productUnavailable ? 'opacity-90' : ''}`}>
                                                    <div className="img-part bg-gray-light  w-[80px] h-[80px] sm:w-[120px] sm:h-[120px] flex items-center justify-center flex-shrink-0 overflow-hidden rounded-sm cursor-pointer relative" onClick={() => handleDivClick(value.product_id)}>
                                                        <SafeImage src={files(value.cover_image, 'thumbnail')} alt="" />
                                                    </div>
                                                    <div className="flex items-start gap-2 flex-grow">
                                                        <div className="text-content flex-grow">
                                                            <h6 className='font-nunito !text-base text-dark mb-2 font-300 cursor-pointer' onClick={() => handleDivClick(value.product_id)}>{language == 'ar' ? value.ar_name : value.name} </h6>
                                                            <div className='mb-2'>
                                                                <ProductPriceDisplay
                                                                    regularPrice={value.regular_price}
                                                                    salePrice={value.sell_price}
                                                                    discount={value.discount}
                                                                    quantity={value.quantity}
                                                                    regularPriceIsSalePrice={!!value.discount}
                                                                />
                                                            </div>
                                                            <PriceChangedLine item={value} />
                                                            {value.size && <p className="text-sm font-400 text-dark mb-2">{t("comman_text.size")} : {value.size}</p>}
                                                            {value.color && <p className="text-sm font-400 text-dark mb-2">{t("comman_text.color")} : {t(`color.${String(value.color).toLowerCase()}`, { defaultValue: value.color })}</p>}
                                                            {value.productUnavailable ? (
                                                                <div className="bg-red-500 text-white text-xs px-3 py-1 rounded-sm w-fit mb-2">
                                                                    No longer available
                                                                </div>
                                                            ) : (
                                                                <StockStatus
                                                                    availableQuantity={value.availableQuantity}
                                                                    soldOut={value.outOfStock}
                                                                    className="mb-2"
                                                                />
                                                            )}
                                                            {value.exceedsStock && !value.outOfStock && !value.productUnavailable && (
                                                                <p className="text-xs text-red-600 mb-2">
                                                                    Only {value.availableQuantity} available. Please reduce quantity.
                                                                </p>
                                                            )}
                                                            {!value.outOfStock && !value.productUnavailable && (
                                                                <QuntityHandler
                                                                    count={value.quantity}
                                                                    setQuntityCount={setQuntityCount}
                                                                    setId={setId}
                                                                    productId={value.product_id}
                                                                    rateId={value.rateId}
                                                                    maxQuantity={value.availableQuantity}
                                                                    onAdjust={(delta) => adjustCartQuantity(value.product_id, value.rateId, delta)}
                                                                    onBurstStart={handleBurstStart}
                                                                    onBurstSettled={handleQuantitySettled}
                                                                />
                                                            )}
                                                        </div>

                                                        {isRemove && deletedItem === value._id ? <CircularProgress /> :
                                                            <button title="Delete" onClick={() => { removeToCart({ product_id: value.product_id, rate_id: value.rateId }); setDeletedItem(value._id) }}>
                                                                <IconDelete className="w-5 h-5 hover:text-black text-gray-700" />
                                                            </button>
                                                        }

                                                    </div>
                                                </div>
                                            </li>)
                                        )}
                                    </ul>
                                </div>
                                :
                                <div className='text-base-18 font-500 text-dark card-item h-full w-full flex items-center justify-center'>
                                    {t("comman_text.cart_empty")}
                                </div>}
                    </div>
                    {
                        cartList.length ?
                            <div className="font-nunito sidebar-action p-4">
                                {hasUnavailable && (
                                    <p className="text-xs text-red-600 mb-2 text-center">
                                        Remove unavailable items to continue
                                    </p>
                                )}
                                <Button className={"w-full"} disabled={hasUnavailable} onClick={() => handleOnCheckout()}>
                                    {t("comman_text.checkout")} ({total})
                                </Button>
                            </div> : ""
                    }

                </div>
            }
        </>
    )
}

export default withTranslation()(CartSidebar)